import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";

const useErrorMessage = (error: string) => {
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    switch (error) {
      case "there is no pixelstreaming server available":
        setErrorMessage(
          "There are too many people using this site at the moment. The 3D scene cannot be displayed. Please try again later.",
        );
        break;
      case "lobby:pending":
        setErrorMessage(
          "We need a moment to prepare the 3D session for you. You'll be able to start creating your visualizations in just a few moments - please wait.",
        );
        break;
      case "lobby:declined":
        setErrorMessage(
          "We couldn't complete your request at this time. Please try again in a few minutes.",
        );
        break;
      case "offline":
        setErrorMessage(
          "It seems your internet connection is lost. Please check your connection before continuing and try refreshing the page to see if this solves the problem.",
        );
        break;
      default:
        setErrorMessage(
          "An unexpected error has occurred. Please try refreshing the page to see if this solves the problem.",
        );
        break;
    }
  }, [error]);

  return errorMessage;
};

type StreamingErrorPopupProps = {
  error: string;
  src?: string;
  handleRestart?: () => void;
};

export default function StreamingErrorPopup({ error, src }: StreamingErrorPopupProps) {
  const errorMessage = useErrorMessage(error);
  const theme = useTheme();

  return (
    <>
      <Container>
        <Background style={{ backgroundImage: `url(${src && src})` }} />
        <Overlay style={{ zIndex: 2 }}>
          <Typography variant="h3" gutterBottom>
            Sorry about that!
          </Typography>

          <Typography variant="body1">{errorMessage}</Typography>
          {/* {error !== "lobby:pending" && (
            <CallToAction>
              <Button variant="contained" onClick={handleRestart}>
                Retry
              </Button>
            </CallToAction>
          )} */}
        </Overlay>
      </Container>
      {error !== "lobby:pending" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: alpha(
              theme.palette.background.default,
              theme.palette.mode === "light" ? 0.3 : 0.6,
            ),
            zIndex: 1,
          }}
        />
      )}
    </>
  );
}

const Container = styled("div")(({ theme }) => ({
  position: "absolute",
  justifyContent: "center",
  alignItems: "center",
  gridColumnStart: 1,
  gridColumnEnd: 2,
  gridRowStart: 1,
  gridRowEnd: 2,
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "1fr",
  minHeight: "100%",
  [theme.breakpoints.down("md")]: {
    top: "-15%",
    width: "70%",
  },
}));

const Background = styled("div")({
  opacity: 0.3,
  gridColumnStart: 1,
  gridColumnEnd: 2,
  gridRowStart: 1,
  gridRowEnd: 2,
  minHeight: "100%",
  backgroundSize: "cover",
  backgroundPosition: "center",
});

const Overlay = styled("div")(({ theme }) => ({
  gridColumnStart: 1,
  gridColumnEnd: 2,
  gridRowStart: 1,
  gridRowEnd: 2,
  placeSelf: "center",
  padding: theme.spacing(4),
  display: "flex",
  width: "90%",
  maxWidth: "600px",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  backdropFilter: "blur(5px)",
}));

// const CallToAction = styled("div")({
//   marginTop: "2em",
//   display: "flex",
//   justifyContent: "space-between",
// });
